import { useState, useEffect } from "react";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import {
  Divider,
  IconButton,
  FormControl,
  Grid,
  FormGroup,
  FormControlLabel,
  Checkbox,
  FormHelperText,
  Backdrop,
  CircularProgress,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/store";
import CustomAutocomplete from "../../../shared/AutoComplete";
import { useParticipants } from "../../fiscalUpgrade/CustomHooks/usePartcipants";
import CustomTextField from "../../../shared/CustomTextFeild";
import { axiosInterceptor } from "../../fiscalUpgrade/services/authInterceptor";
import { usePreviousMonths } from "../../fiscalUpgrade/CustomHooks/usePreviousMonths";
import * as constants from "../../../constants";
import CustomDatePicker from "../../../shared/CustomDate";
import dayjs, { Dayjs } from "dayjs";
import ContainedButton from "../../../shared/Button/CustomContainedButton";
import OutlinedButton from "../../../shared/Button/customOutlinedButton";
import environment from "../../../environments/environment";
import { notifyUser } from "../../fiscalUpgrade/Utils/notifyUser";

type PopUpProps = {
  open: boolean;
  handleClose: () => void;
};
type TimeFrameOption = {
  name: string;
  code: string;
} | null;
export default function ParticipantSummaryReport(props: PopUpProps) {
  const contactInformation = useSelector((state: RootState) => state.participantAdminProfile);
  const { open, handleClose } = props;
  const handleDialogClose = () => {
    handleClose(); // Close the dialog
    restAll();
  };
  const { t } = useTranslation();
  const translate = t as (key: string) => string;
  const [loading, setLoading] = useState(false);
  const User = useSelector((state: RootState) => state.auth);
  const [participant, setParticipant] = useState<any[]>([]);
  const [participantId, setParticipantId] = useState<string | null>(contactInformation?.participantAdminProfile ?? "All");
  const [filterId, setFilterId] = useState<string | number | null>("-1");
  const { fetchParticipants } = useParticipants();
  const [fromDate, setFromDate] = useState<Dayjs | null>(null);
  const [toDate, setToDate] = useState<Dayjs | null>(null);
  const [option, setOption] = useState<number | null>(1);
  const [state, setState] = useState({
    summary: false,
    transactions: false,
  });
  const [optionMonthError, setOptionMonthError] = useState<string | null>(null);
  const [fromDateError, setFromDateError] = useState<string | null>(null);
  const [toDateError, setToDateError] = useState<string | null>(null);
  const [checkboxError, setCheckboxError] = useState<string | null>(null);
  const [optionError, setOptionError] = useState<string | null>(null);
  const [participantError, setParticipantError] = useState<string | null>(null);
  const PARTICIPANT_PDF = environment.getParticipantStatementPDF;
  const handleChange = (event: any) => {
    setState({
      ...state,
      [event.target.name]: event.target.checked,
    });
    setCheckboxError("");
  };
  const timeFrameOptions = [
    { name: "As of Today", code: "As of Today" },
    { name: "Month", code: "Month" },
    { name: "Custom Date Range", code: "Custom Date Range" },
  ];
  const [selectedTimeFrameCode, setSelectedTimeFrameCode] =
    useState<TimeFrameOption>(timeFrameOptions[0]);

  const {
    montlyEndDateList,
    monthEndDate,
    fetchPreviousMonths,
    setMonthEndDate,
  } = usePreviousMonths(
    User.clientId,
    constants.PARTICIPANT_MONTHLY_LIST_NUMBER
  );
  function restAll() {
    setSelectedTimeFrameCode(timeFrameOptions[0]);
    setFromDateError(null);
    setOptionError(null);
    setParticipantError(null);
    setToDate(null);
    setFromDate(null);
    setOptionMonthError(null);
    setMonthEndDate(monthEndDate);
    setParticipantId("All");
    setFilterId("-1");
    setOption(1);
    setState({
      summary: false,
      transactions: false,
    });
    setToDateError(null);
    setCheckboxError(null);
  }
  const validateDates = (from: Dayjs | null, to: Dayjs | null) => {
    let fromError = null;
    let toError = null;

    if (!from && selectedTimeFrameCode?.code === "Custom Date Range") {
      fromError = translate("fromDateIsRequired");
    }

    if (!to && selectedTimeFrameCode?.code === "Custom Date Range") {
      toError = translate("toDateIsRequired");
    }

    if (
      from &&
      to &&
      from.isAfter(to) &&
      selectedTimeFrameCode?.code === "Custom Date Range"
    ) {
      fromError = "Start date cannot be later than end date";
      // toError = "End date cannot be earlier than start date";
    }

    setFromDateError(fromError);
    setToDateError(toError);

    return !fromError && !toError;
  };
  const validateSelection = () => {
    const isCheckboxValid = state.summary || state.transactions;
    const isOptionValid = option !== null;
    const isFilterIdValid =
      filterId !== null && filterId !== undefined && filterId !== "";
    const isMonthVaild = monthEndDate !== "";
    if (!isCheckboxValid) {
      setCheckboxError(translate("pleaseSelectSummaryOrTransaction"));
    } else {
      setCheckboxError(null);
    }

    if (!isOptionValid) {
      setOptionError(translate("pleaseSelectTimeFrame"));
    } else {
      setOptionError(null);
    }

    if (!isFilterIdValid) {
      setParticipantError(translate("participantSelectionRequired"));
    } else {
      setParticipantError(null);
    }
    if (!isMonthVaild) {
      setOptionMonthError(translate("pleaseSelectMonths"));
    } else {
      setOptionMonthError(null);
    }
    return isCheckboxValid && isOptionValid && isFilterIdValid && isMonthVaild;
  };
  useEffect(() => {
    if (open) {
      fetchParticipants();
      fetchPreviousMonths();
    }
  }, [open]);

  useEffect(() => {
    if (User?.participantArray) {
      const updatedParticipants = [
        { participantName: "All", participantId: -1 },
        ...User.participantArray,
      ];
      setParticipant(updatedParticipants);
    }
  }, [User?.participantArray]);
  useEffect(() => {
    const newId = contactInformation?.participantAdminProfile ?? "All";
    if (participantId !== newId) {
      setParticipantId(newId);
    }
  }, [contactInformation?.participantAdminProfile]);

  const handlePartcipantChange = (value: any) => {
    if (value === null || value === undefined) {
      setParticipantId(null);
      setFilterId(null);
    } else {
      setParticipantId(value);
      const findName = participant?.find(
        (item: any) => item.participantName === value
      );
      if (findName?.participantId) {
        setFilterId(findName?.participantId);
      }
      setParticipantError("");
    }
  };
  function handleTimeFrameCode(value: any) {
    if (value === null || value === undefined) {
      setSelectedTimeFrameCode(null);
      setState({
        summary: false,
        transactions: false,
      });
      setOption(null);
      setFromDate(null);
      setToDate(null);
    } else {
      setSelectedTimeFrameCode(value);
      setOptionError(null);
      if (value?.code === "Custom Date Range") {
        setState({
          summary: false,
          transactions: true,
        });
        setOption(3);
      } else if (value?.code === "Month") {
        setState({
          summary: false,
          transactions: false,
        });
        setOption(2);
        setFromDate(null);
        setToDate(null);
      } else {
        setState({
          summary: false,
          transactions: false,
        });
        setOption(1);
        setFromDate(null);
        setToDate(null);
      }
    }
  }
async function handleGenerate() {
  if (validateDates(fromDate, toDate) && validateSelection()) {
    setLoading(true);

    try {
      const formattedFromDate =
        fromDate === null ? null : dayjs(fromDate).format("YYYY-MM-DD");

      const formattedToDate =
        toDate === null ? null : dayjs(toDate).format("YYYY-MM-DD");

      const optionBasedToDate =
        option === 2 && monthEndDate
          ? new Date(monthEndDate).toISOString().split("T")[0]
          : formattedToDate;

      let partcipantVo = {
        participantId: filterId,
        accountId: -1,
        fromDate: formattedFromDate,
        toDate: optionBasedToDate,
        option: option,
        isSummaryRequired: state.summary,
        isTransactionsRequired: state.transactions,
        clientId: User.clientId,
      };

      const response = await axiosInterceptor.post(
        `${PARTICIPANT_PDF}`,
        partcipantVo,
        {
          timeout: 300000, // ✅ 5 min
          maxContentLength: Infinity, // ✅ IMPORTANT
          maxBodyLength: Infinity,    // ✅ IMPORTANT
        }
      );

      if (response?.status === constants.STATUS_CODES.SUCCESS) {
        const base64String = response?.data?.data?.fileContent;

        if (!base64String) {
          notifyUser(
            response?.data?.message ?? "No data available",
            "error",
            translate("participantSummary")
          );
          return;
        }

        // ✅ Efficient base64 → blob conversion
        const byteCharacters = atob(base64String);

        const sliceSize = 1024 * 1024; // 1MB chunks
        const byteArrays = [];

        for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
          const slice = byteCharacters.slice(offset, offset + sliceSize);

          const byteNumbers = new Array(slice.length);
          for (let i = 0; i < slice.length; i++) {
            byteNumbers[i] = slice.charCodeAt(i);
          }

          byteArrays.push(new Uint8Array(byteNumbers));
        }

        const blob = new Blob(byteArrays, {
          type: "text/csv;charset=utf-8;",
        });

        // ✅ Download
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = "transaction.csv";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        notifyUser(
          "File downloaded successfully",
          "success",
          translate("participantSummary")
        );

        restAll();
        handleClose();
        setParticipantId(contactInformation?.participantAdminProfile);
      }
    } catch (error: any) {
      if (error.code === "ECONNABORTED") {
        notifyUser("Request timed out (large file)", "error");
      } else {
        notifyUser(
          error?.response?.data?.message ?? translate("apiRequestFailed"),
          "error"
        );
      }
    } finally {
      setLoading(false);
    }
  }
}

return (
    <Dialog
      open={open}
      fullWidth
      maxWidth={false}
      sx={{
        maxWidth: "700px", // Adjust width as needed
        margin: "auto", // Center horizontally
        display: "flex", // Ensure flex behavior
        alignItems: "center", // Center vertically
        justifyContent: "center", // Center horizontally
        borderRadius: 2,
        "& .MuiPaper-root": { borderRadius: "12px", background: "white" },
      }}
    >
      {/* Dialog Header */}
      <DialogTitle
        sx={{
          backgroundColor: "#059ca9",
          color: "white",
          display: "flex",
          justifyContent: "space-between",
          padding: "10px 10px",
        }}
      >
        {translate("participantSummaryCriteria")}
        <IconButton
          onClick={handleDialogClose}
          sx={{ color: "white", padding: "0px" }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {/* Dialog Content */}
      <DialogContent style={{ paddingTop: "20px" }}>
        <Grid container columnSpacing={2}>
          {/* Type Dropdown */}
          <Grid item xs={6}>
            <CustomAutocomplete
              // sx={{ width: "250px" }}

              options={participant}
              labelText={translate("participants")}
              placeholder={translate("select")}
              getOptionLabel={(option) => option.participantName || ""}
              isOptionEqualToValue={(option, value) =>
                option.participantName === value.participantName
              }
              value={
                participant.find((p) => p.participantName === participantId) ||
                null
              }
              onChange={(event, newValue) =>
                handlePartcipantChange(newValue?.participantName || null)
              }
              slotProps={{
                paper: {
                  sx: { backgroundColor: "white !important" },
                },
              }}
              error={Boolean(participantError)}
            />
            {participantError && (
              <FormHelperText style={{ color: "#d32f2f", fontSize: "15px" }}>
                {participantError}
              </FormHelperText>
            )}
          </Grid>
          <Grid item xs={6}>
            <CustomTextField
              labelText={translate("accounts")}
              readOnly
              value={"All"}
            />
          </Grid>
          <Grid item xs={6}>
            <CustomAutocomplete
              labelText={translate("timeFrame")}
              size="small"
              options={timeFrameOptions}
              value={selectedTimeFrameCode}
              onChange={(event, newValue) => {
                handleTimeFrameCode(newValue);
              }}
              getOptionLabel={(option) => option.name}
              isOptionEqualToValue={(option, value) =>
                option?.code === value?.code
              }
              error={Boolean(optionError)}
              slotProps={{
                paper: {
                  sx: { backgroundColor: "white !important" },
                },
              }}
              placeholder={translate("select")}
            />
            {optionError && (
              <FormHelperText style={{ color: "#d32f2f", fontSize: "15px" }}>
                {optionError}
              </FormHelperText>
            )}
          </Grid>

          {selectedTimeFrameCode?.code === "Month" && (
            <Grid item xs={6}>
              <CustomAutocomplete
                labelText={translate("clientMonth")}
                options={montlyEndDateList}
                getOptionLabel={(option) => option.label || ""}
                value={
                  montlyEndDateList.find(
                    (d: any) => d.value === monthEndDate
                  ) || null
                }
                onChange={(event, newValue) => {
                  setMonthEndDate(newValue?.value ?? "");
                  setOptionMonthError("");
                }}
                error={Boolean(optionMonthError)}
                slotProps={{
                  paper: {
                    sx: { backgroundColor: "white !important" },
                  },
                }}
                placeholder={translate("select")}
              />
              {optionMonthError && (
                <FormHelperText style={{ color: "#d32f2f", fontSize: "15px" }}>
                  {optionMonthError}
                </FormHelperText>
              )}
            </Grid>
          )}
          {(selectedTimeFrameCode?.code === "As of Today" ||
            selectedTimeFrameCode?.code === "Custom Date Range" ||
            selectedTimeFrameCode?.code === undefined) && (
              <Grid item xs={6}></Grid>
            )}
          {selectedTimeFrameCode?.name === "Custom Date Range" && (
            <>
              {/* From Date */}
              <Grid item xs={6} mt={2}>
                <CustomDatePicker
                  labelText={translate("fromDate")}
                  value={fromDate}
                  onChange={(newValue) => {
                    setFromDate(newValue);
                    validateDates(newValue, toDate);
                  }}
                  error={!!fromDateError}
                  maxDate={dayjs()}
                  size="small"
                  PopperProps={{
                    modifiers: [
                      {
                        name: "offset",
                        options: {
                          offset: [0, 10],
                        },
                      },
                    ],
                  }}
                  slotProps={{
                    popper: {
                      sx: {
                        "& .MuiPaper-root": {
                          backgroundColor: "#fff",
                        },
                      },
                    },
                  }}
                  placeholder={translate("select")}
                />
                {fromDateError && (
                  <FormHelperText
                    style={{
                      color: "#d32f2f",
                      fontSize: "15px",
                    }}
                  >
                    {fromDateError}
                  </FormHelperText>
                )}
              </Grid>
              {/* To Date */}
              <Grid item xs={6} mt={2}>
                <CustomDatePicker
                  labelText={translate("toDate")}
                  value={toDate}
                  onChange={(newValue) => {
                    setToDate(newValue);
                    validateDates(fromDate, newValue);
                  }}
                  error={!!toDateError}
                  maxDate={dayjs()}
                  size="small"
                  PopperProps={{
                    modifiers: [
                      {
                        name: "offset",
                        options: {
                          offset: [0, 10],
                        },
                      },
                    ],
                  }}
                  slotProps={{
                    popper: {
                      sx: {
                        "& .MuiPaper-root": {
                          backgroundColor: "#fff",
                        },
                      },
                    },
                  }}
                />
                {toDateError && (
                  <FormHelperText
                    style={{
                      color: "#d32f2f",
                      fontSize: "15px",
                    }}
                  >
                    {toDateError}
                  </FormHelperText>
                )}
              </Grid>
            </>
          )}
          <Grid item xs={6}>
            <FormControl component="fieldset">
              <FormGroup aria-label="position" row>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={state.summary}
                      onChange={handleChange}
                      name="summary"
                    />
                  }
                  disabled={selectedTimeFrameCode?.name === "Custom Date Range"}
                  label={translate("summarys")}
                  labelPlacement="end"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={state.transactions}
                      onChange={handleChange}
                      name="transactions"
                    />
                  }
                  disabled={selectedTimeFrameCode?.name === "Custom Date Range"}
                  label={translate("transactions")}
                  labelPlacement="end"
                />
              </FormGroup>
            </FormControl>
            {checkboxError && (
              <FormHelperText
                style={{
                  color: "#d32f2f",
                  fontSize: "15px",
                  marginTop: "-9px",
                }}
              >
                {checkboxError}
              </FormHelperText>
            )}
          </Grid>
        </Grid>
      </DialogContent>

      <Divider />

      {/* Dialog Actions */}
      <DialogActions>
        <ContainedButton onClick={handleGenerate}>
          {translate("generate")}
        </ContainedButton>
        <OutlinedButton onClick={handleDialogClose}>
          {translate("cancel")}
        </OutlinedButton>
      </DialogActions>
      <Backdrop
        sx={(theme) => ({
          color: "#fff",
          zIndex: theme.zIndex.drawer + 1,
          display: "flex",
          flexDirection: "column",
        })}
        open={loading}
      >
        <CircularProgress color="inherit" />
        <Typography color="white">Please wait...</Typography>
      </Backdrop>
    </Dialog>
  );
}
